import { Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from './components/AdminLayout';
import GlobalDashboardPage from './pages/GlobalDashboardPage';
import SuperAdminPage from './pages/SuperAdminPage';
import IntakeFormPage from './pages/IntakeFormPage';
import AnimalListPage from './pages/AnimalListPage';
import AnimalProfilePage from './pages/AnimalProfilePage';
import AnimalRequirementsPage from './pages/AnimalRequirementsPage';
import AnimalIntakeDetailsPage from './pages/AnimalIntakeDetailsPage';
import AnimalMedicalPage from './pages/AnimalMedicalPage';
import AddAnimalPage from './pages/AddAnimalPage';
import SmartMatchPage from './pages/SmartMatchPage';
import FinancialDashboardPage from './pages/FinancialDashboardPage';
import NotificationsCenterPage from './pages/NotificationsCenterPage';
import PostAdoptionDashboardPage from './pages/PostAdoptionDashboardPage';
import MedicalRemindersPage from './pages/MedicalRemindersPage';
import AdoptionReviewPage from './pages/AdoptionReviewPage';
import ShelterSettingsPage from './pages/ShelterSettingsPage';
import ShelterDashboardPage from './pages/ShelterDashboardPage';
import LoginPage from './pages/LoginPlaceholder';
import SetPasswordPage from './pages/SetPasswordPage';
import { ErrorBoundary, NotFoundPage, useAuth, supabase, ResetPasswordPage } from '@rehome/shared';
import { RequireShelterUser, RequirePasswordNotExpired } from './components/AuthGuards';
import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Route wrapper: super_admin → SuperAdminPage,
 * shelter_admin → redirected to their own shelter dashboard,
 * only super_admin sees the global aggregated view.
 */
const DashboardRouter: React.FC = () => {
  const { user, userProfile } = useAuth();
  const [shelterId, setShelterId] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!user || !userProfile) { setChecking(false); return; }

    if (userProfile.role === 'super_admin') {
      setChecking(false);
      return;
    }

    // For shelter admin/staff: find their shelter_id
    supabase
      .from('shelter_staff')
      .select('shelter_id')
      .eq('user_id', user.id)
      .single()
      .then(({ data, error }) => {
        if (!error && data) {
          setShelterId(data.shelter_id);
        }
        setChecking(false);
      });
  }, [user, userProfile]);

  if (checking) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (userProfile?.role === 'super_admin') {
    return <SuperAdminPage />;
  }

  // Shelter admin/staff: redirect to their own shelter dashboard
  if (shelterId) {
    return <Navigate to={`/shelter/${shelterId}`} replace />;
  }

  // Fallback: no shelter assigned → show empty state
  return (
    <div className="text-center py-16">
      <h2 className="text-xl font-bold text-gray-800 mb-2">Nincs menhely hozzárendelve</h2>
      <p className="text-gray-500">A fiókodhoz még nincs menhely rendelve. Kérj segítséget a superadmin-tól.</p>
    </div>
  );
};

/**
 * ReHome Shelter App (shelter.rehome.hu)
 *
 * Shelter management: intake, animal profiles, CRM, post-adoption,
 * notifications, ReHome-only financial overview, global dashboard.
 * Only accessible by shelter staff (user_type = 'shelter').
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      {/* NYILVÁNOS: a jelszó-visszaállító linkről érkezők épp azért
          jönnek, mert nem tudnak belépni. */}
      <Route path="/reset-password" element={<ResetPasswordPage appName="ReHome Shelter" />} />
      <Route element={
        <RequireShelterUser>
          <RequirePasswordNotExpired>
            <AdminLayout />
          </RequirePasswordNotExpired>
        </RequireShelterUser>
      }>
        <Route path="/" element={<DashboardRouter />} />
        <Route path="/global" element={<DashboardRouter />} />
        <Route path="/shelter/:shelterId">
          <Route index element={<ShelterDashboardPage />} />
          <Route path="intake" element={<IntakeFormPage />} />
          <Route path="animals" element={<AnimalListPage />} />
          <Route path="animals/:animalId" element={<AnimalProfilePage />} />
          <Route path="animals/:animalId/requirements" element={<AnimalRequirementsPage />} />
          <Route path="animals/:animalId/intake-details" element={<AnimalIntakeDetailsPage />} />
          <Route path="animals/:animalId/medical" element={<AnimalMedicalPage />} />
          <Route path="add" element={<AddAnimalPage />} />
          <Route path="match" element={<SmartMatchPage />} />
          <Route path="finance" element={<FinancialDashboardPage />} />
          <Route path="notifications" element={<NotificationsCenterPage />} />
          <Route path="post-adoption" element={<PostAdoptionDashboardPage />} />
          <Route path="reminders" element={<MedicalRemindersPage />} />
          <Route path="leads" element={<AdoptionReviewPage />} />
          <Route path="settings" element={<ShelterSettingsPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage message="A keresett oldal nem található a shelter felületen." />} />
      </Route>
    </Routes>
    </ErrorBoundary>
  );
};

export default App;
